import { addDays, type Sprint } from '@hive/shared';
import { CalendarPlus } from 'lucide-react';
import { useRef, useState } from 'react';
import type { Dependency } from '@/data/dependencies';
import type { GridTask } from '@/data/tasks';
import { cn } from '@/lib/utils';
import {
  barBox,
  DAY_WIDTH,
  dayX,
  dependencyArrows,
  draggedDates,
  ganttRange,
  ganttTicks,
  layoutBars,
  ROW_HEIGHT,
  type DragMode,
  type GanttZoom,
} from './gantt';

const TITLE_WIDTH = 220;
const HEADER_HEIGHT = 40;

const STATUS_FILL: Record<string, string> = {
  backlog: 'fill-line-hot',
  todo: 'fill-cyan/50',
  in_progress: 'fill-cyan',
  review: 'fill-magenta/80',
  done: 'fill-green/40',
};

interface Drag {
  taskId: string;
  mode: DragMode;
  originX: number;
  deltaDays: number;
}

/**
 * Grid's timeline: bars from start to due date, dependency arrows, sprint
 * bands and a today line. Drag a bar to move it, or its ends to change dates.
 */
export function GanttView({
  tasks,
  dependencies,
  sprints,
  today,
  onOpen,
  onReschedule,
}: {
  tasks: readonly GridTask[];
  dependencies: readonly Dependency[];
  sprints: readonly Pick<Sprint, 'id' | 'name' | 'start_date' | 'end_date'>[];
  today: string;
  onOpen: (taskId: string) => void;
  onReschedule: (task: GridTask, dates: { start_date: string | null; due_date: string | null }) => void;
}) {
  const [zoom, setZoom] = useState<GanttZoom>('day');
  const [drag, setDrag] = useState<Drag | null>(null);
  const moved = useRef(false);
  const { bars: baseBars, unscheduled } = layoutBars(tasks);
  // While dragging, the bar follows the pointer.
  const bars = baseBars.map((b) => {
    if (!drag || drag.taskId !== b.task.id) return b;
    const dates = draggedDates(b.task, drag.mode, drag.deltaDays);
    if (!dates) return b;
    const start = dates.start_date ?? dates.due_date!;
    const end = dates.due_date ?? dates.start_date!;
    return { ...b, start: start <= end ? start : end, end: start <= end ? end : start };
  });
  const range = ganttRange(baseBars, sprints, today);
  const width = range.days * DAY_WIDTH[zoom];
  const height = Math.max(bars.length, 1) * ROW_HEIGHT;
  const arrows = dependencyArrows(dependencies, bars, range.start, zoom);
  const ticks = ganttTicks(range, zoom);
  const visibleSprints = sprints.filter((s) => s.end_date >= range.start && s.start_date <= range.end);

  const startDrag = (e: React.PointerEvent, taskId: string, mode: DragMode) => {
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    moved.current = false;
    setDrag({ taskId, mode, originX: e.clientX, deltaDays: 0 });
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const deltaDays = Math.round((e.clientX - drag.originX) / DAY_WIDTH[zoom]);
    if (deltaDays !== drag.deltaDays) {
      moved.current = true;
      setDrag({ ...drag, deltaDays });
    }
  };
  const endDrag = () => {
    if (!drag) return;
    const task = baseBars.find((b) => b.task.id === drag.taskId)?.task;
    const dates = task ? draggedDates(task, drag.mode, drag.deltaDays) : null;
    if (task && dates) onReschedule(task, dates);
    else if (task && !moved.current && drag.mode === 'move') onOpen(task.id);
    setDrag(null);
  };

  return (
    <div className="flex min-h-0 flex-1 gap-3 pb-4">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex" role="group" aria-label="Zoom">
            {(['day', 'week'] as const).map((z) => (
              <button
                key={z}
                type="button"
                aria-pressed={zoom === z}
                onClick={() => setZoom(z)}
                className={cn(
                  'cursor-pointer px-2 py-0.5 font-mono text-[11px] shadow-[inset_0_0_0_1px_var(--color-line-hot)]',
                  zoom === z ? 'bg-cyan/15 text-cyan' : 'text-muted hover:text-text',
                )}
              >
                {z === 'day' ? 'Days' : 'Weeks'}
              </button>
            ))}
          </div>
          <p className="font-mono text-[11px] text-muted">
            Drag a bar to move it, or its ends to change its dates.
            {arrows.some((a) => a.conflict) ? (
              <span className="ml-2 text-yellow">Yellow arrows: a task starts before what it waits on is due.</span>
            ) : null}
          </p>
        </div>
        <div
          className="min-h-0 flex-1 overflow-auto bg-bg-2/60 shadow-[inset_0_0_0_1px_var(--color-line)]"
          data-testid="gantt-scroll"
        >
          <div style={{ width: TITLE_WIDTH + width }} className="relative">
            <div className="sticky top-0 z-20 flex bg-bg-2" style={{ height: HEADER_HEIGHT }}>
              <div
                className="sticky left-0 z-10 flex items-end border-r border-b border-line bg-bg-2 px-3 pb-1 font-display text-[9px] font-bold tracking-[0.16em] text-muted uppercase"
                style={{ width: TITLE_WIDTH }}
              >
                Task
              </div>
              <svg width={width} height={HEADER_HEIGHT} className="border-b border-line">
                {visibleSprints.map((s) => {
                  const x = Math.max(0, dayX(s.start_date, range.start, zoom));
                  const x2 = dayX(addDays(s.end_date, 1), range.start, zoom);
                  return (
                    <g key={s.id}>
                      <rect x={x} y={2} width={x2 - x} height={14} className="fill-green/10 stroke-green/40" />
                      <text x={x + 4} y={12} className="fill-green font-mono text-[9px]">
                        {s.name}
                      </text>
                    </g>
                  );
                })}
                {ticks.map((t) => (
                  <text
                    key={t.x}
                    x={t.x + 2}
                    y={HEADER_HEIGHT - 6}
                    className={cn('font-mono text-[9px]', t.major ? 'fill-text' : 'fill-muted')}
                  >
                    {t.label}
                  </text>
                ))}
              </svg>
            </div>
            <div className="flex">
              <ol
                className="sticky left-0 z-10 border-r border-line bg-bg-2"
                style={{ width: TITLE_WIDTH }}
                aria-label="Scheduled tasks"
              >
                {bars.map((b) => (
                  <li
                    key={b.task.id}
                    style={{ height: ROW_HEIGHT }}
                    className="flex items-center border-b border-line/50 px-2"
                  >
                    <button
                      type="button"
                      onClick={() => onOpen(b.task.id)}
                      className={cn(
                        'min-w-0 flex-1 cursor-pointer truncate text-left text-sm hover:text-cyan',
                        b.task.status === 'done' && 'text-muted line-through',
                      )}
                    >
                      {b.task.title}
                    </button>
                  </li>
                ))}
                {bars.length === 0 ? (
                  <li style={{ height: ROW_HEIGHT }} className="flex items-center px-2 text-xs text-dim">
                    No tasks with dates.
                  </li>
                ) : null}
              </ol>
              <svg
                width={width}
                height={height}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={() => setDrag(null)}
                role="img"
                aria-label={`Timeline of ${bars.length} tasks`}
                className={cn(drag && 'cursor-grabbing')}
              >
                <defs>
                  <marker
                    id="gantt-arrow"
                    viewBox="0 0 6 6"
                    refX="5"
                    refY="3"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto"
                  >
                    <path d="M0,0 L6,3 L0,6 z" className="fill-muted" />
                  </marker>
                  <marker
                    id="gantt-arrow-warn"
                    viewBox="0 0 6 6"
                    refX="5"
                    refY="3"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto"
                  >
                    <path d="M0,0 L6,3 L0,6 z" className="fill-yellow" />
                  </marker>
                </defs>
                {visibleSprints.map((s) => {
                  const x = Math.max(0, dayX(s.start_date, range.start, zoom));
                  const x2 = dayX(addDays(s.end_date, 1), range.start, zoom);
                  return <rect key={s.id} x={x} y={0} width={x2 - x} height={height} className="fill-green/5" />;
                })}
                {bars.map((b) => (
                  <line
                    key={b.task.id}
                    x1={0}
                    x2={width}
                    y1={(b.row + 1) * ROW_HEIGHT}
                    y2={(b.row + 1) * ROW_HEIGHT}
                    className="stroke-line/50"
                  />
                ))}
                {today >= range.start && today <= range.end ? (
                  <line
                    x1={dayX(today, range.start, zoom) + DAY_WIDTH[zoom] / 2}
                    x2={dayX(today, range.start, zoom) + DAY_WIDTH[zoom] / 2}
                    y1={0}
                    y2={height}
                    className="stroke-yellow"
                    strokeOpacity={0.7}
                    data-testid="gantt-today"
                  />
                ) : null}
                {arrows.map((a) => (
                  <path
                    key={a.dependency.id}
                    d={a.path}
                    fill="none"
                    className={a.conflict ? 'stroke-yellow' : 'stroke-muted'}
                    strokeWidth={1.25}
                    markerEnd={`url(#${a.conflict ? 'gantt-arrow-warn' : 'gantt-arrow'})`}
                    data-testid={a.conflict ? 'arrow-conflict' : 'arrow'}
                  />
                ))}
                {bars.map((b) => {
                  const box = barBox(b, range.start, zoom);
                  const fill = STATUS_FILL[b.task.status] ?? 'fill-cyan';
                  if (b.marker) {
                    const cx = box.x + DAY_WIDTH[zoom] / 2;
                    const cy = box.y + box.height / 2;
                    const r = Math.min(8, box.height / 2);
                    return (
                      <polygon
                        key={b.task.id}
                        points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`}
                        className={cn(fill, 'cursor-grab stroke-cyan')}
                        onPointerDown={(e) => startDrag(e, b.task.id, 'move')}
                        data-testid="gantt-marker"
                      >
                        <title>{`${b.task.title}: ${b.start}`}</title>
                      </polygon>
                    );
                  }
                  return (
                    <g key={b.task.id} data-testid="gantt-bar">
                      <rect
                        x={box.x + 1}
                        y={box.y}
                        width={Math.max(box.width - 2, 4)}
                        height={box.height}
                        rx={2}
                        className={cn(fill, 'cursor-grab')}
                        onPointerDown={(e) => startDrag(e, b.task.id, 'move')}
                      >
                        <title>{`${b.task.title}: ${b.start} to ${b.end}`}</title>
                      </rect>
                      <rect
                        x={box.x}
                        y={box.y}
                        width={6}
                        height={box.height}
                        className="cursor-ew-resize fill-transparent hover:fill-text/30"
                        onPointerDown={(e) => startDrag(e, b.task.id, 'start')}
                        aria-label={`Change the start of ${b.task.title}`}
                      />
                      <rect
                        x={box.x + box.width - 6}
                        y={box.y}
                        width={6}
                        height={box.height}
                        className="cursor-ew-resize fill-transparent hover:fill-text/30"
                        onPointerDown={(e) => startDrag(e, b.task.id, 'end')}
                        aria-label={`Change the due date of ${b.task.title}`}
                      />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>
      </div>
      <aside aria-label="Unscheduled" className="flex w-56 shrink-0 flex-col gap-2 overflow-y-auto">
        <h3 className="font-display text-[9px] font-bold tracking-[0.16em] text-muted uppercase">
          No dates <span className="text-dim">{unscheduled.length}</span>
        </h3>
        <ul className="flex flex-col gap-px">
          {unscheduled.map((t) => (
            <li key={t.id} className="flex items-center gap-1 bg-bg-3/60 px-2 py-1.5">
              <button
                type="button"
                className="min-w-0 flex-1 cursor-pointer truncate text-left text-sm hover:text-cyan"
                onClick={() => onOpen(t.id)}
              >
                {t.title}
              </button>
              <button
                type="button"
                className="cursor-pointer p-0.5 text-muted hover:text-cyan"
                aria-label={`Schedule ${t.title} from today`}
                title="Start today, due in three days"
                onClick={() => onReschedule(t, { start_date: today, due_date: addDays(today, 2) })}
              >
                <CalendarPlus className="size-4" aria-hidden />
              </button>
            </li>
          ))}
          {unscheduled.length === 0 ? <li className="text-xs text-dim">Every task has dates.</li> : null}
        </ul>
      </aside>
    </div>
  );
}
